import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, Server, Link2, Send, CheckCircle2, XCircle, Loader2, Wifi } from 'lucide-react';

const IntegrationsSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFromAddress, setSmtpFromAddress] = useState('');
  const [screenconnectUrl, setScreenconnectUrl] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      console.log('Settings loaded:', response.data);
      setSmtpHost(response.data.smtp_host || '');
      setSmtpPort(response.data.smtp_port || 587);
      setSmtpUser(response.data.smtp_user || '');
      setSmtpPassword(response.data.smtp_password || '');
      setSmtpFromAddress(response.data.smtp_from_address || '');
      console.log('From address set to:', response.data.smtp_from_address);
      setScreenconnectUrl(response.data.screenconnect_url || '');
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSMTP = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      console.log('Saving from address:', smtpFromAddress);
      const response = await api.put('/settings', {
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort),
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from_address: smtpFromAddress,
      });
      console.log('Save response:', response.data);
      toast.success('SMTP settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save SMTP settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await api.post('/integrations/smtp/test', {
        recipient: testEmail,
        smtp_host: smtpHost,
        smtp_port: parseInt(smtpPort),
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from_address: smtpFromAddress
      });
      
      setTestResult({
        success: true,
        message: response.data.message || 'Test email sent successfully!',
        details: response.data.details || ''
      });
      toast.success('Test email sent!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to send test email';
      setTestResult({
        success: false,
        message: 'Failed to send test email',
        details: errorMessage
      });
      toast.error('Test email failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveScreenConnect = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.put('/settings', {
        screenconnect_url: screenconnectUrl
      });
      toast.success('ScreenConnect settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save ScreenConnect settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-green-700 rounded-xl flex items-center justify-center">
                <Link2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Integrations</h1>
                <p className="text-sm text-slate-500">Configure third-party integrations</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* SMTP Configuration */}
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Mail className="w-5 h-5 text-blue-600" />
              <span>SMTP Email Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure your SMTP server settings to enable email sending
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveSMTP} className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-host" className="flex items-center space-x-2">
                    <Server className="w-4 h-4 text-slate-500" />
                    <span>SMTP Host</span>
                  </Label>
                  <Input
                    id="smtp-host"
                    type="text"
                    placeholder="smtp.gmail.com"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                    required
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">Your SMTP server address</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-port" className="flex items-center space-x-2">
                    <Server className="w-4 h-4 text-slate-500" />
                    <span>SMTP Port</span>
                  </Label>
                  <Input
                    id="smtp-port"
                    type="number"
                    placeholder="587"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                    required
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">Typically 587 for TLS or 465 for SSL</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-user" className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span>SMTP Username/Email</span>
                  </Label>
                  <Input
                    id="smtp-user"
                    type="email"
                    placeholder="your-email@gmail.com"
                    value={smtpUser}
                    onChange={(e) => setSmtpUser(e.target.value)}
                    required
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">Your email address for SMTP authentication</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-password" className="flex items-center space-x-2">
                    <Lock className="w-4 h-4 text-slate-500" />
                    <span>SMTP Password</span>
                  </Label>
                  <Input
                    id="smtp-password"
                    type="password"
                    placeholder="••••••••••"
                    value={smtpPassword}
                    onChange={(e) => setSmtpPassword(e.target.value)}
                    required
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">
                    For Gmail, use an App Password (not your regular password)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smtp-from" className="flex items-center space-x-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    <span>Default From Address</span>
                  </Label>
                  <Input
                    id="smtp-from"
                    type="email"
                    placeholder="noreply@yourdomain.com"
                    value={smtpFromAddress}
                    onChange={(e) => setSmtpFromAddress(e.target.value)}
                    className="border-slate-300"
                  />
                  <p className="text-xs text-slate-500">
                    The email address that will appear as the sender (optional, defaults to SMTP username)
                  </p>
                </div>
              </div>

              {/* Test Email Section */}
              <div className="pt-4 border-t border-slate-200">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-amber-900 mb-3 text-sm flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Test SMTP Configuration
                  </h4>
                  <p className="text-xs text-amber-800 mb-3">
                    Send a test email to verify your SMTP settings before saving
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="recipient@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1 h-9 text-sm"
                    />
                    <Button
                      type="button"
                      onClick={handleTestEmail}
                      disabled={testing || !smtpHost || !smtpPort || !smtpUser || !smtpPassword}
                      variant="outline"
                      size="sm"
                      className="border-amber-300 hover:bg-amber-100"
                    >
                      {testing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-1" />
                          Test
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Test Result Output */}
                  {testResult && (
                    <div className={`mt-3 p-3 rounded-md border ${
                      testResult.success 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start gap-2">
                        {testResult.success ? (
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${
                            testResult.success ? 'text-green-900' : 'text-red-900'
                          }`}>
                            {testResult.message}
                          </p>
                          {testResult.details && (
                            <p className={`text-xs mt-1 ${
                              testResult.success ? 'text-green-700' : 'text-red-700'
                            }`}>
                              {testResult.details}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2 text-sm">Gmail Setup Instructions:</h4>
                  <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Enable 2-Step Verification in your Google Account</li>
                    <li>Go to Security → App passwords</li>
                    <li>Generate a new app password for "Mail"</li>
                    <li>Use that password here (not your regular Gmail password)</li>
                  </ol>
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-md"
                >
                  {saving ? 'Saving...' : 'Save SMTP Settings'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* ScreenConnect Integration */}
        <Card className="shadow-sm border-slate-200 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Server className="w-5 h-5 text-green-600" />
              <span>ScreenConnect Integration</span>
            </CardTitle>
            <CardDescription>
              Configure ScreenConnect for remote access to managed devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveScreenConnect} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="screenconnect-url" className="flex items-center space-x-2">
                  <Server className="w-4 h-4 text-slate-500" />
                  <span>ScreenConnect Server URL</span>
                </Label>
                <Input
                  id="screenconnect-url"
                  type="url"
                  placeholder="https://yourinstance.screenconnect.com"
                  value={screenconnectUrl}
                  onChange={(e) => setScreenconnectUrl(e.target.value)}
                  className="border-slate-300"
                />
                <p className="text-xs text-slate-500">
                  Enter your ScreenConnect server URL for remote access integration
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2 text-sm">Setup Instructions:</h4>
                <ol className="text-xs text-green-800 space-y-1 list-decimal list-inside">
                  <li>Log in to your ScreenConnect instance</li>
                  <li>Copy your instance URL (e.g., https://yourinstance.screenconnect.com)</li>
                  <li>Paste it above and save</li>
                  <li>The system will use this URL for remote access links</li>
                </ol>
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-md"
              >
                {saving ? 'Saving...' : 'Save ScreenConnect URL'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* UniFi Integration */}
        <Card className="shadow-sm border-slate-200 mt-6">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Wifi className="w-5 h-5 text-blue-600" />
              <span>UniFi Network Integration</span>
            </CardTitle>
            <CardDescription>
              Manage multiple UniFi Controllers and assign them to clients
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Connect to UniFi Controllers to automatically sync network devices as assets. 
              You can add multiple controllers and assign each one to different clients.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/settings/unifi-controllers')}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
              >
                <Wifi className="w-4 h-4 mr-2" />
                Manage UniFi Controllers
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default IntegrationsSettings;
